// ============================================================
// SPATIAL CONTEXT ENGINE — Fase 10.1 / 10.6
// Enriquece cada zona com contexto operacional em tempo real
// ============================================================
import type { ObraZone, SpatialRisk, Equipment, LogisticRoute } from '@/types/twin'
import type { RiskContext } from '@/types/operational'
import type { VisionEvent, PPEViolation } from '@/types/vision'
import { getZoneNeighbors, buildSpatialGraph, type SpatialGraph } from './operationalSpatialGraph'

export interface ZoneContext {
  zone:          ObraZone
  riskScore:     number    // 0-100
  riskLevel:     'low' | 'moderate' | 'high' | 'critical'
  activeRisks:   SpatialRisk[]
  workerCount:   number
  equipment:     Equipment[]
  congestion:    'none' | 'low' | 'medium' | 'high' | 'blocked'
  ppeViolations: number
  visionEvents:  VisionEvent[]
  neighborRisks: { zoneId: string; level: string }[]
  summary:       string
}

// ─── Risk propagation ─────────────────────────────────────────
const PROPAGATION_FACTOR = 0.4   // neighbor risk transmits at 40%

export function computeZoneRiskScore(
  zone:      ObraZone,
  risks:     SpatialRisk[],
  graph?:    SpatialGraph,
  allZones?: ObraZone[],
): number {
  const zoneRisks = risks.filter(r => r.zone_id === zone.id)

  let score = 0
  for (const r of zoneRisks) {
    const s = r.severity === 'emergency' ? 100
      : r.severity === 'critical' ? 75
      : r.severity === 'warning'  ? 45
      : 20
    score = Math.max(score, s * r.score)
  }

  // Propagate from neighbors
  if (graph && allZones) {
    const neighbors = getZoneNeighbors(graph, zone.id)
    for (const neighborId of neighbors) {
      const neighborRisks = risks.filter(r => r.zone_id === neighborId)
      const neighborScore = neighborRisks.reduce((max, r) => {
        const s = r.severity === 'emergency' ? 100
          : r.severity === 'critical' ? 75
          : r.severity === 'warning'  ? 45 : 20
        return Math.max(max, s * r.score)
      }, 0)
      score = Math.max(score, neighborScore * PROPAGATION_FACTOR)
    }
  }

  return Math.min(100, Math.round(score))
}

export function scoreToLevel(score: number): ZoneContext['riskLevel'] {
  if (score >= 70) return 'critical'
  if (score >= 45) return 'high'
  if (score >= 20) return 'moderate'
  return 'low'
}

// ─── Build full context for a zone ──────────────���────────────
export function buildZoneContext(
  zone:          ObraZone,
  opts: {
    risks:         SpatialRisk[]
    equipment:     Equipment[]
    routes:        LogisticRoute[]
    visionEvents:  VisionEvent[]
    ppeViolations: PPEViolation[]
    workerCounts:  Map<string, number>
    graph?:        SpatialGraph
    allZones?:     ObraZone[]
  },
): ZoneContext {
  const { risks, equipment, routes, visionEvents, ppeViolations, workerCounts } = opts

  const activeRisks = risks.filter(r => r.zone_id === zone.id && r.is_active)
  const zoneEquipment = equipment.filter(e => e.current_zone_id === zone.id)
  const zoneEvents    = visionEvents.filter(e => !e.is_resolved)
  const ppe           = ppeViolations.filter(v => !v.acknowledged).length

  // Congestion: from logistic routes passing through this zone
  const zoneRoutes = routes.filter(r => r.from_zone_id === zone.id || r.to_zone_id === zone.id)
  const worstCongestion = zoneRoutes.reduce<CongestionLevel>((max, r) => {
    const order: Record<string, number> = { none: 0, low: 1, medium: 2, high: 3, blocked: 4 }
    return (order[r.congestion_level] ?? 0) > (order[max] ?? 0) ? r.congestion_level : max
  }, 'none')

  type CongestionLevel = 'none' | 'low' | 'medium' | 'high' | 'blocked'

  const riskScore = computeZoneRiskScore(zone, risks, opts.graph, opts.allZones)
  const riskLevel = scoreToLevel(riskScore)

  // Neighbor risk summary
  const neighborRisks = opts.graph && opts.allZones
    ? getZoneNeighbors(opts.graph, zone.id).map(nId => {
        const nZone = opts.allZones!.find(z => z.id === nId)
        const nScore = computeZoneRiskScore(
          nZone ?? { id: nId, risk_level: 'low' } as ObraZone,
          risks, opts.graph, opts.allZones,
        )
        return { zoneId: nId, level: scoreToLevel(nScore) }
      })
    : []

  const summary = buildZoneSummary(zone, riskLevel, worstCongestion as CongestionLevel, activeRisks)

  return {
    zone,
    riskScore,
    riskLevel,
    activeRisks,
    workerCount:   workerCounts.get(zone.id) ?? 0,
    equipment:     zoneEquipment,
    congestion:    worstCongestion as CongestionLevel,
    ppeViolations: ppe,
    visionEvents:  zoneEvents,
    neighborRisks,
    summary,
  }
}

function buildZoneSummary(
  zone:       ObraZone,
  riskLevel:  ZoneContext['riskLevel'],
  congestion: ZoneContext['congestion'],
  risks:      SpatialRisk[],
): string {
  if (riskLevel === 'low' && congestion === 'none') return 'Operação normal'
  const parts: string[] = []
  if (riskLevel === 'critical' || riskLevel === 'high') {
    const topRisk = risks[0]
    if (topRisk) parts.push(topRisk.title)
  }
  if (congestion === 'blocked') parts.push('rota bloqueada')
  else if (congestion === 'high') parts.push('congestionamento alto')
  return parts.join(' · ') || 'Atenção'
}

// ─── Spatial risk propagation batch ───────────────────────────
export function propagateSpatialRisks(
  risks:  SpatialRisk[],
  graph:  SpatialGraph,
  zones:  ObraZone[],
): SpatialRisk[] {
  const propagated: SpatialRisk[] = []

  for (const risk of risks.filter(r => r.is_active && r.score > 0.6)) {
    const neighbors = getZoneNeighbors(graph, risk.zone_id ?? '')
    for (const neighborId of neighbors) {
      // Check if neighbor already has a propagated risk from this source
      const alreadyPropagated = propagated.some(
        p => p.propagated_from === risk.id && p.zone_id === neighborId,
      )
      if (alreadyPropagated) continue

      propagated.push({
        id:              `prop_${risk.id}_${neighborId}`,
        obra_id:         risk.obra_id,
        zone_id:         neighborId,
        risk_type:       risk.risk_type,
        severity:        risk.severity === 'emergency' ? 'critical' : 'warning',
        title:           `↗ Risco propagado: ${risk.title}`,
        detail:          `Propagado da zona adjacente`,
        score:           risk.score * PROPAGATION_FACTOR,
        propagated_from: risk.id,
        is_active:       true,
        created_at:      risk.created_at,
      })
    }
  }

  return propagated
}
